import { SourceFavicon } from "@/components/ai-elements/source-favicon";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  useCitationIsMobile,
  useCitationSources,
} from "@/contexts/citation-sources-context";
import { useTranslation } from "@/hooks/useTranslation";
import { stripEmailBlocks } from "@/lib/ai";
import {
  resolveCitationSources,
  type CitationSource,
} from "@/lib/citation-sources";
import { collectCitedSourceIds } from "@shaarp/common";
import { ChevronDownIcon, GlobeIcon } from "lucide-react";
import { useState, type ComponentProps } from "react";

/** The pill, shaped like the model badge it sits next to in the footer. */
function SourcesRecapButton({
  count,
  ...props
}: ComponentProps<"button"> & { readonly count: number }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className="group/sources inline-flex gap-1 items-center px-2 py-0.5 text-xs rounded-full border text-muted-foreground transition-colors hover:text-foreground data-[state=open]:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-majorelle/40"
      {...props}
    >
      <GlobeIcon className="size-3 shrink-0" />
      {t("chat.message.citations.recap", { count })}
      <ChevronDownIcon className="size-3 shrink-0 transition-transform group-data-[state=open]/sources:rotate-180" />
    </button>
  );
}

/** One line per source: its rank, its site's logo, its title linking out. */
function SourcesRecapList({
  sources,
}: {
  readonly sources: ReadonlyArray<CitationSource>;
}) {
  return (
    <ol className="space-y-1.5 text-xs text-muted-foreground">
      {sources.map((source, index) => (
        <li key={source.id} className="flex items-center gap-1.5">
          <span className="shrink-0 tabular-nums">{index + 1}.</span>
          <SourceFavicon host={source.host} />
          <a
            href={source.href}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 truncate underline-offset-2 hover:text-foreground hover:underline"
          >
            {source.title}
            <span className="ml-1.5 text-muted-foreground/60">
              {source.host}
            </span>
          </a>
        </li>
      ))}
    </ol>
  );
}

/**
 * The sources an answer cites, as a pill in the footer under it, next to the
 * model badge. Only the pages a pill of the answer opens: the ones a search
 * found and the answer never leaned on are not sources of it. Nothing renders
 * when the answer cites nothing, or cites only ids no search returned. A marker
 * inside an email block counts for nothing: the body is shown as plain text,
 * with no pill.
 *
 * A pill rather than a line of its own: the inline pills already say where each
 * passage comes from, and a sources line between the answer, its quick replies
 * and the footer made the bottom of every answer a pile of rows. The list opens
 * over the thread — a popover on desktop, a bottom sheet on mobile — because
 * the footer is overlaid into the gap below the message and has no room to
 * unfold into.
 */
export function ChatSourcesRecap({ text }: { text: string }) {
  const { t } = useTranslation();
  const isMobile = useCitationIsMobile();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Mounted on the first tap, then kept so it can animate out: a thread holds
  // one of these per answer, and most are never opened.
  const [hasSheet, setHasSheet] = useState(false);
  const known = useCitationSources();
  const sources = resolveCitationSources(
    collectCitedSourceIds(stripEmailBlocks(text)),
    known,
  );
  if (sources.length === 0) return null;

  if (isMobile) {
    return (
      <>
        {/* The open look the popover trigger gets from Radix. */}
        <SourcesRecapButton
          count={sources.length}
          data-state={isSheetOpen ? "open" : "closed"}
          onClick={() => {
            setHasSheet(true);
            setIsSheetOpen(true);
          }}
        />
        {hasSheet && (
          <MobileSheet
            open={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            heightClass="h-auto max-h-[80dvh]"
          >
            <SheetTitle className="px-4 pb-2 text-sm font-medium">
              {t("chat.message.citations.recap", { count: sources.length })}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("chat.message.citations.recapDescription")}
            </SheetDescription>
            <div className="overflow-y-auto overscroll-y-contain px-4 pb-4">
              <SourcesRecapList sources={sources} />
            </div>
          </MobileSheet>
        )}
      </>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <SourcesRecapButton count={sources.length} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className="w-96 max-w-[calc(100vw-24px)] max-h-80 overflow-y-auto overscroll-y-contain p-3"
      >
        <SourcesRecapList sources={sources} />
      </PopoverContent>
    </Popover>
  );
}
